/**
 * Landing KPI tile. Big value, label, delta chip, optional sparkline.
 * Layout proportions match the plan: value at 32px, label 11-12px uppercase,
 * delta top-right, sparkline beneath.
 */
import React from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { Sparkline } from './Sparkline';
import { cn } from '../../../../lib/cn';
import { deltaDirection, formatDelta } from '../format';

export interface KpiTileProps {
  label: string;
  value: React.ReactNode;
  deltaPct?: number | null;
  /** Invert color direction — a negative delta is "good" (e.g. churn). */
  negativeIsGood?: boolean;
  hint?: string;
  trendValues?: number[];
  icon?: React.ReactNode;
  loading?: boolean;
  className?: string;
}

export const KpiTile: React.FC<KpiTileProps> = ({
  label,
  value,
  deltaPct,
  negativeIsGood = false,
  hint,
  trendValues,
  icon,
  loading = false,
  className,
}) => {
  if (loading) {
    return (
      <div className="h-[104px] animate-pulse rounded-lg border border-gray-200 bg-white p-3.5">
        <div className="h-3 w-24 rounded bg-gray-100" />
        <div className="mt-2.5 h-7 w-20 rounded bg-gray-100" />
        <div className="mt-3 h-5 w-full rounded bg-gray-100" />
      </div>
    );
  }

  const dir = deltaDirection(deltaPct);
  const effectiveDir: typeof dir =
    dir === 'flat' ? 'flat' : negativeIsGood ? (dir === 'up' ? 'down' : 'up') : dir;

  const chipClasses =
    effectiveDir === 'up'
      ? 'text-emerald-700 bg-emerald-50 ring-emerald-100'
      : effectiveDir === 'down'
      ? 'text-rose-700 bg-rose-50 ring-rose-100'
      : 'text-gray-500 bg-gray-50 ring-gray-100';

  const ChipIcon = effectiveDir === 'up' ? ArrowUpRight : effectiveDir === 'down' ? ArrowDownRight : Minus;

  return (
    <div
      className={cn(
        'group relative rounded-lg border border-gray-200 bg-white p-3.5 shadow-sm transition-shadow hover:shadow-md',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            {icon && <span className="text-gray-400">{icon}</span>}
            {label}
          </p>
          <p className="mt-1.5 text-2xl font-bold leading-none text-gray-900 tabular-nums">
            {value}
          </p>
        </div>
        {deltaPct != null && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10.5px] font-semibold tabular-nums ring-1',
              chipClasses,
            )}
          >
            <ChipIcon className="h-3 w-3" />
            {formatDelta(deltaPct)}
          </span>
        )}
      </div>
      {trendValues && trendValues.length > 1 && (
        <div className="mt-2 -mx-1">
          <Sparkline values={trendValues} width={220} height={28} />
        </div>
      )}
      {hint && !trendValues && (
        <p className="mt-2 text-xs text-gray-500">{hint}</p>
      )}
    </div>
  );
};
